import { lazyLoadView } from '@utils/LazyLoad'
import { FEATURES } from '@constants/Text'

export const BACK_OFFICE_TABS = [
  {
    key: 'imports',
    label: 'Importação de Dados',
    path: 'imports',
    nameSuffix: 'imports',
  },
  {
    key: 'validation',
    label: 'Validação',
    path: 'validation',
    nameSuffix: 'validation',
  },
  {
    key: 'suggestions',
    label: 'Sugestões',
    path: 'suggestions',
    nameSuffix: 'suggestions',
  },
  {
    key: 'investment_accounts',
    label: 'Contas de investimento',
    path: 'investment-accounts',
    nameSuffix: 'investment_accounts',
  },
]

export const BACK_OFFICE_DETAIL_VIEWS = ['table', 'kanban']

export const BACK_OFFICE_DEFAULT_DETAIL_VIEW = 'kanban'

export const canAccessBackOfficeTab = ({ user = {}, canShow, show } = {}) => {
  if (user.IS_INTERNAL) return true
  if (typeof canShow === 'function') {
    return !!canShow(FEATURES.BACK_OFFICE) && !!canShow(FEATURES.BACK_OFFICE_SELF_SERVICE)
  }
  if (show) {
    return !!show[FEATURES.BACK_OFFICE] && !!show[FEATURES.BACK_OFFICE_SELF_SERVICE]
  }
  return false
}

export const normalizeBackOfficeDetailView = ({ view }) => {
  const normalized = String(view || '').toLowerCase()
  return BACK_OFFICE_DETAIL_VIEWS.includes(normalized)
    ? normalized
    : BACK_OFFICE_DEFAULT_DETAIL_VIEW
}

export const routeUsesBackOfficeDetailView = ({ routeName }) => {
  const safeName = String(routeName || '')
  if (!safeName) return false
  const suffix = safeName.split('.').pop()
  return suffix === 'imports'
}

export const ensureBackOfficeViewInRoute = ({ route, router }) => {
  const routeName = route && route.name ? route.name : ''
  if (!routeUsesBackOfficeDetailView({ routeName }))
    return { changed: false, normalized: null }

  const currentView = route && route.query ? route.query.view : ''
  const normalized = normalizeBackOfficeDetailView({ view: currentView })
  if (currentView === normalized) return { changed: false, normalized }

  const newQuery = Object.assign({}, route.query, { view: normalized })
  router.replace({ query: newQuery })
  return { changed: true, normalized }
}

export const buildBackOfficeChildren = ({
  namePrefix,
  tabs = BACK_OFFICE_TABS,
  featureFlag = null,
  publicTabs = ['imports'],
  guard = canAccessBackOfficeTab,
  meta = {},
} = {}) => {
  const firstTab = tabs[0]
  const redirect = {
    path: '',
    redirect: {
      name: `${namePrefix}.${firstTab.nameSuffix}`,
      query: { view: BACK_OFFICE_DEFAULT_DETAIL_VIEW },
    },
    meta: Object.assign({}, meta),
  }

  const children = tabs.map((tab) =>
    makeChildRoute({
      tab,
      namePrefix,
      featureFlag,
      publicTabs,
      guard,
      baseMeta: meta,
    })
  )

  return [redirect, ...children]
}

const makeChildRoute = ({
  tab,
  namePrefix,
  featureFlag,
  publicTabs,
  guard,
  baseMeta,
}) => {
  const metaObject = Object.assign({}, baseMeta, {
    requiresDetailView: tab.key === 'imports',
  })
  if (featureFlag) metaObject.can_show = featureFlag
  if (!publicTabs.includes(tab.key)) metaObject.customPermission = guard

  return {
    path: tab.path,
    name: `${namePrefix}.${tab.nameSuffix}`,
    meta: metaObject,
    component: resolveComponentByKey(tab.key),
  }
}

const VIEW_LOADERS = {
  imports: () =>
    lazyLoadView(import('@modules/back-office/views/imports/view-imports.vue')),
  validation: () =>
    lazyLoadView(
      import('@modules/back-office/views/validation/view-validation.vue')
    ),
  suggestions: () =>
    lazyLoadView(
      import('@modules/back-office/views/suggestions/view-suggestions.vue')
    ),
  investment_accounts: () =>
    lazyLoadView(
      import(
        '@modules/back-office/views/investment-accounts/view-investment-accounts.vue'
      )
    ),
}

const resolveComponentByKey = (key) => {
  const loader = VIEW_LOADERS[key] || VIEW_LOADERS.investment_accounts
  return loader
}

export const backOfficeOptionsRoutes = ({
  user = {},
  show = null,
  canShow = null,
  tabs = BACK_OFFICE_TABS,
  publicTabs = ['imports'],
} = {}) => {
  const canAccessRestricted = canAccessBackOfficeTab({ user, show, canShow })
  return tabs
    .map((tab) => ({
      name: `back-office.${tab.nameSuffix}`,
      title: tab.label,
      hasAccess: publicTabs.includes(tab.key) ? true : canAccessRestricted,
    }))
    .filter((option) => option.hasAccess)
}
